import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { nativePushProviderSchema, voipTokenEnvironmentSchema } from '../notifications/schema';
import {
  CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS,
  conversionAttributionSchema,
} from '../attribution/schema';

// Device platforms Expo Push can deliver to. Closed set: web push is out of
// scope and would arrive as its own delivery seam, not a third enum value.
export const PUSH_PLATFORM = ['ios', 'android'] as const;
export const pushPlatformSchema = z.enum(PUSH_PLATFORM);
export type PushPlatform = z.infer<typeof pushPlatformSchema>;

// Expo push tokens look like `ExponentPushToken[xxxx]` but the format is an
// Expo implementation detail — bound the length, don't parse the shape.
export const deviceRegistrationRequestSchema = z
  .object({
    // Optional when voipToken+voipProvider are present: iOS members who refuse
    // notification permission can still register for incoming-call PushKit.
    token: z.string().min(1).max(512).optional(),
    platform: pushPlatformSchema,
    // Raw APNs/FCM token + its environment, captured for Iterable (Phase 2)
    // forward-compat. Optional: older clients omit them, and capture can fail.
    // The environment is client-derived (a TestFlight build mints production
    // APNs tokens, a dev build sandbox ones) — not inferable server-side.
    nativeToken: z.string().min(1).max(512).optional(),
    nativeProvider: nativePushProviderSchema.optional(),
    // PushKit VoIP token + APNs environment for incoming-call delivery.
    voipToken: z.string().min(1).max(512).optional(),
    voipProvider: voipTokenEnvironmentSchema.optional(),
    // Signing identity that minted the VoIP token. APNs rejects a token whose
    // topic does not match (`TopicDisallowed`); the server uses this as
    // `apns-topic` and falls back to env `APNS_BUNDLE_ID` when the row has none.
    voipBundleId: z
      .string()
      .min(1)
      .max(255)
      .regex(/^[A-Za-z0-9][A-Za-z0-9.-]*$/)
      .optional(),
    // The device's live landing capture (ADR-132) rides the registration so the
    // first binding can be attributed to the send that drove the install.
    // Optional: an unattributed registration is the common case. Validated here
    // so a malformed record is a 400, never a silently dropped analytics dimension.
    attribution: conversionAttributionSchema.optional(),
    // Fresh per registration attempt, minted on the device. Stored on the row
    // so a later unregister can name exactly the registrations it may remove.
    // Optional: clients from before the unregister fence omit it.
    registrationId: z.uuid().optional(),
  })
  .strict()
  .refine(
    (v) =>
      (v.voipToken === undefined && v.voipProvider === undefined) ||
      (v.voipToken !== undefined && v.voipProvider !== undefined),
    { message: 'voipToken and voipProvider must both be present or both absent' },
  )
  .refine((v) => v.voipBundleId === undefined || v.voipToken !== undefined, {
    message: 'voipBundleId requires voipToken',
  })
  .refine((v) => v.token !== undefined || v.voipToken !== undefined, {
    message: 'token or voipToken is required',
  })
  // PushKit is APNs-only: a VoIP token can only exist on an iOS device, and
  // the call-push leg selection keys on the voip columns alone, so an Android
  // row carrying them would be routed to a leg that cannot deliver. Rejected
  // at the boundary rather than trusted to the client's platform gate.
  .refine((v) => v.voipToken === undefined || v.platform === 'ios', {
    message: 'voipToken is iOS (PushKit) only',
  });
export type DeviceRegistrationRequest = z.infer<typeof deviceRegistrationRequestSchema>;

// Upper bound on the registration ids one unregister may name: the device's
// recent attempts plus any retry still pending for the member.
export const DEVICE_UNREGISTER_MAX_REGISTRATION_IDS = 32;

/**
 * Body for DELETE /api/v1/devices/current. Names the registration attempts the
 * device made; the server removes the caller's rows whose `registration_id` is
 * one of them, never a row by token alone.
 */
export const deviceUnregisterRequestSchema = z
  .object({
    registrationIds: z.array(z.uuid()).min(1).max(DEVICE_UNREGISTER_MAX_REGISTRATION_IDS),
  })
  .strict();
export type DeviceUnregisterRequest = z.infer<typeof deviceUnregisterRequestSchema>;

export const deviceUnregisteredResponseSchema = z.object({ removed: z.boolean() }).strict();
export type DeviceUnregisteredResponse = z.infer<typeof deviceUnregisteredResponseSchema>;

// Payload of the `member.device.registered` domain event: the closed push
// platform plus the ten conversion attribution keys built from the request's
// record (nulls when the registration was not attributed — always present so
// the analytics forwarder never sees a missing key). Strict, so the Expo
// token, the native token or provider, the row id, the member id, and the
// capture timestamp can never ride along. `status` is injected by buildAnnotatedDomainEvent from the
// analytics catalog.
export const memberDeviceRegisteredPayloadSchema = z
  .object({
    platform: pushPlatformSchema,
    ...CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS,
    status: z.literal('created').optional(),
  })
  .strict();
export type MemberDeviceRegisteredPayload = z.infer<typeof memberDeviceRegisteredPayloadSchema>;

// Payload of the `member.device.push_binding_changed` domain event: the
// members, other than the actor, whose device rows this change reassigned or
// deleted. Strict and identifier-only, so no push token, native token, VoIP
// token, provider, device row id, or Iterable user id can ride along; the
// consumer reads current state from the database. Member ids use the shared
// member-id grammar: migrated members keep non-RFC UUIDv7 ids a strict
// `.uuid()` would drop.
// The cap on `previousMemberIds`. Producers truncate to it (the sync is
// best-effort and must never fail the registration write it rides in); the
// consumer's strict parse still rejects anything longer.
export const PUSH_BINDING_CHANGED_MAX_PREVIOUS_MEMBERS = 50;
export const memberDevicePushBindingChangedPayloadSchema = z
  .object({
    previousMemberIds: z.array(memberIdSchema).max(PUSH_BINDING_CHANGED_MAX_PREVIOUS_MEMBERS),
  })
  .strict();
export type MemberDevicePushBindingChangedPayload = z.infer<
  typeof memberDevicePushBindingChangedPayloadSchema
>;

// Client-side view of the registration response. The server's full-row
// contract is derived from the table (apps/api, ADR-066); the client needs
// only proof of persistence, so this stays a minimal passthrough projection.
export const deviceRegisteredResponseSchema = z
  .object({ id: z.string().uuid(), token: z.string(), platform: pushPlatformSchema })
  .passthrough();
export type DeviceRegisteredResponse = z.infer<typeof deviceRegisteredResponseSchema>;

/**
 * The push permission as the OS reports it, reduced to what a staff reader
 * needs. `blocked` means the OS will not show the prompt again (`canAskAgain`
 * false), so only the device settings can change it; `denied` means not
 * granted while the OS can still ask. The iOS `undetermined` reading and the
 * Android never-asked reading are deliberately absent: the client reports only
 * once a prompt has resolved, or when the OS reports granted or blocked.
 */
// Client responses use this closed set: adding a status requires endpoint
// versioning or a capability gate for installed clients (ADR-135 Rule 3).
export const PUSH_PERMISSION_STATUS = ['granted', 'denied', 'blocked'] as const;
export const pushPermissionStatusSchema = z.enum(PUSH_PERMISSION_STATUS);
export type PushPermissionStatus = z.infer<typeof pushPermissionStatusSchema>;

// The member is the verified bearer subject, so no member id travels in the
// body; strict so a smuggled identifier or token is rejected, never ignored.
export const pushPermissionReportRequestSchema = z
  .object({ platform: pushPlatformSchema, status: pushPermissionStatusSchema })
  .strict();
export type PushPermissionReportRequest = z.infer<typeof pushPermissionReportRequestSchema>;

// Parsed by the mobile client, so unknown keys are stripped rather than
// rejected (ADR-135 Rule 1). `reportedAt` is the stored row's `updated_at`,
// which the trigger refreshes on every report, identical ones included.
export const pushPermissionReportedDataSchema = z.object({
  platform: pushPlatformSchema,
  status: pushPermissionStatusSchema,
  reportedAt: z.string().datetime({ offset: true }),
});
export type PushPermissionReportedData = z.infer<typeof pushPermissionReportedDataSchema>;
